import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { PageLayout } from '../../components/organisms/PageLayout';
import KanbanTabs from '../../components/molecules/kanban/KanbanTabs';
import NewJobModal from '../../components/molecules/kanban/modals/NewJobModal';
import { api } from '../../utils/api';
import { API_ENDPOINTS } from '../../config';
import { useUser } from '../../context/UserContext';
import '../../styles/page-layout.css';
import { emitSubscriptionRefresh } from '../../utils/subscriptionEvents';
import { track } from '../../utils/analytics';
import '../../styles/not-found.css';
import '../../styles/onboarding-spotlight.css';

interface SwimlaneData {
  uid: string;
  name: string | null;
  position?: number;
}

interface KanbanData {
  uid: string;
  name: string;
  status: string;
  swimlanes?: SwimlaneData[];
}

const CRM: React.FC = () => {
  const { boardId, jobId } = useParams<{ boardId: string; jobId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { userProfile } = useUser();
  const [boardData, setBoardData] = useState<KanbanData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('board');
  const [jobModalOpen, setJobModalOpen] = useState(false);
  const [defaultList, setDefaultList] = useState<string | undefined>(undefined);
  const [shouldOpenNewDocument, setShouldOpenNewDocument] = useState(false);
  const [shouldOpenNewContact, setShouldOpenNewContact] = useState(false);
  const [shouldOpenNewActivity, setShouldOpenNewActivity] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    document.title = 'OfferFlow | CRM';
    if (boardId) track('job_board_viewed', { board_id: boardId });
  }, [boardId]);

  const isOnboardingNewJob = searchParams.get('onboarding') === 'new-job';

  useEffect(() => {
    if (isOnboardingNewJob) {
      document.body.classList.add('onboarding-spotlight');
      return () => document.body.classList.remove('onboarding-spotlight');
    }
    return undefined;
  }, [isOnboardingNewJob]);

  const defaultJobModalTab = searchParams.get('tab') as 'edit' | 'ai-tools' | 'activities' | 'notes' | 'contacts' | 'documents' | null;

  const availableLists = boardData?.swimlanes
    ?.map(sl => sl.name || '')
    .filter(name => name !== '') || [];

  useEffect(() => {
    if (jobId) {
      setSelectedJobId(jobId);
    } else {
      setSelectedJobId(undefined);
    }
  }, [jobId]);

  useEffect(() => {
    let cancelled = false;
    const fetchBoardData = async () => {
      if (!boardId) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        setNotFound(false);
        const kanbanData = await api.get<KanbanData>(API_ENDPOINTS.kanbans.get(boardId));
        if (cancelled) return;
        setBoardData(kanbanData);
      } catch (err: any) {
        if (cancelled) return;
        if (err?.status === 404) {
          setBoardData(null);
          setNotFound(true);
          setError(null);
        } else if (err?.status === 401) {
          setError('Authentication failed');
        } else {
          setError('Failed to load board data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBoardData();
    return () => { cancelled = true; };
  }, [boardId]);

  // Used by the loading / not-found / error screens, before boardData exists
  const stateBreadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Boards', href: '/dashboard' },
  ];

  if (loading) {
    return (
      <PageLayout breadcrumbItems={stateBreadcrumbItems} title="Board">
        <div className="not-found">
          <div className="not-found__card">
            <h1 className="not-found__title">Loading board…</h1>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (notFound && userProfile) {
    return (
      <PageLayout breadcrumbItems={stateBreadcrumbItems} title="Board">
        <div className="not-found">
          <div className="not-found__card">
            <h1 className="not-found__title">Board not found</h1>
            <p className="not-found__text">
              We couldn&apos;t find this board. It may have been deleted or you no longer have access.
            </p>
            <button
              className="not-found__button"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout breadcrumbItems={stateBreadcrumbItems} title="Board">
        <div className="not-found">
          <div className="not-found__card">
            <h1 className="not-found__title">Something went wrong</h1>
            <p className="not-found__text">
              {error}
            </p>
            <button
              className="not-found__button"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Boards', href: '/dashboard' },
    { label: boardData?.name ?? 'Board' },
  ];

  const clearOnboardingParam = () => {
    if (!isOnboardingNewJob) return;
    const next = new URLSearchParams(searchParams);
    next.delete('onboarding');
    const query = next.toString();
    navigate(`${window.location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  };

  const handleNewJob = () => {
    setDefaultList(availableLists[0]);
    setJobModalOpen(true);
    clearOnboardingParam();
  };

  const handleOpenJobModal = (listName?: string) => {
    setDefaultList(listName);
    setJobModalOpen(true);
    clearOnboardingParam();
  };

  const handleSaveJob = async (data: {
    company: string;
    title: string;
    vacancyUrl: string;
    descriptionHtml: string;
    board: string;
    list: string;
  }) => {
    if (!boardId || !boardData) {

      setJobModalOpen(false);
      return;
    }

    const swimlane = boardData.swimlanes?.find(
      sl => sl.name?.toUpperCase() === data.list.toUpperCase()
    );

    if (!swimlane || !swimlane.uid) {

      setJobModalOpen(false);
      return;
    }

    try {
      const descriptionHtml = data.descriptionHtml.trim();
      const hasContent = descriptionHtml
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim().length > 0;

      await api.post(API_ENDPOINTS.jobs.create, {
        kanban_uid: boardId,
        swimlane_uid: swimlane.uid,
        company_name: data.company,
        job_title: data.title,
        description_text: hasContent ? descriptionHtml : null,
        vacancy_url: data.vacancyUrl ? data.vacancyUrl : null,
      });

      track('job_created', { board_id: boardId, list: data.list });
      emitSubscriptionRefresh();

      setJobModalOpen(false);

      setRefreshKey(prev => prev + 1);
    } catch (error: any) {

      
      if (error?.status === 422) {

      } else if (error?.status === 401) {

      } else if (error?.status === 404) {

      } else {

      }
      
    }
  };

  return (
    <>
      <PageLayout
        breadcrumbItems={breadcrumbItems}
        title={boardData?.name ?? 'Board'}
        mainNoPadding
        actions={
          <>
            {activeTab === 'board' && (
              <button
                className={`breadcrumbs__action-btn ${isOnboardingNewJob ? 'breadcrumbs__action-btn--pulse' : ''}`}
                onClick={handleNewJob}
              >
                + New job
              </button>
            )}
            {activeTab === 'contacts' && (
              <button 
                className="breadcrumbs__action-btn"
                onClick={() => setShouldOpenNewContact(true)}
              >
                + New contact
              </button>
            )}
            {activeTab === 'documents' && (
              <button 
                className="breadcrumbs__action-btn"
                onClick={() => setShouldOpenNewDocument(true)}
              >
                + New document
              </button>
            )}
            {activeTab === 'activities' && (
              <button 
                className="breadcrumbs__action-btn"
                onClick={() => setShouldOpenNewActivity(true)}
              >
                + New activity
              </button>
            )}
          </>
        }
      >
        <KanbanTabs 
          onTabChange={setActiveTab} 
          onOpenJobModal={handleOpenJobModal}
          shouldOpenNewDocument={shouldOpenNewDocument}
          onNewDocumentOpened={() => setShouldOpenNewDocument(false)}
          shouldOpenNewContact={shouldOpenNewContact}
          onNewContactOpened={() => setShouldOpenNewContact(false)}
          shouldOpenNewActivity={shouldOpenNewActivity}
          onNewActivityOpened={() => setShouldOpenNewActivity(false)}
          initialJobId={selectedJobId}
          boardId={boardId}
          refreshKey={refreshKey}
          defaultJobModalTab={defaultJobModalTab || undefined}
        />
      </PageLayout>

      {boardData && (
        <NewJobModal
          open={jobModalOpen}
          boards={[boardData.name]}
          lists={availableLists}
          initial={{
            board: boardData.name,
            list: defaultList || availableLists[0] || '',
          }}
          onClose={() => setJobModalOpen(false)}
          onSave={handleSaveJob}
        />
      )}

      {isOnboardingNewJob && !jobModalOpen && (
        <>
          <div className="onboarding-overlay" aria-hidden="true" />
          <button
            type="button"
            className="onboarding-overlay__close"
            onClick={clearOnboardingParam}
            aria-label="Dismiss onboarding hint"
            title="Dismiss"
          >
            ×
          </button>
          <div className="onboarding-overlay__hint">
            Click <strong>+ New job</strong> in the top bar (or a <strong>+</strong> in any column) to save your first job.
          </div>
        </>
      )}
    </>
  );
};

export default CRM;
